import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from './config';
import { dogService } from './dogService';
import { BirthRecord, BirthRecordFormData, DeliveryType } from '@/types/dog';

/**
 * 出産記録検索フィルタ
 */
export interface BirthRecordSearchFilter {
  motherId?: string;
  fatherId?: string;
  deliveryType?: DeliveryType;
  birthDateFrom?: Date;
  birthDateTo?: Date;
}

/**
 * 出産記録管理サービスクラス
 */
export class BirthRecordService {
  private readonly COLLECTION_NAME = 'birthRecords';
  private readonly GESTATION_PERIOD_DAYS = 63; // 犬の平均妊娠期間（日）

  /**
   * 新規出産記録作成
   */
  async createBirthRecord(data: BirthRecordFormData, userId: string): Promise<BirthRecord> {
    try {
      // バリデーション
      this.validateBirthRecordData(data);

      // 親犬の所有確認
      await this.verifyParentOwnership(data.motherId, data.fatherId, userId);

      // ドキュメントIDを生成
      const recordRef = doc(collection(db, this.COLLECTION_NAME));
      const recordId = recordRef.id;

      const now = Timestamp.now();

      const birthRecord: BirthRecord = {
        id: recordId,
        ownerId: userId,
        motherId: data.motherId,
        fatherId: data.fatherId,
        matingDate: data.matingDate ? Timestamp.fromDate(data.matingDate) : undefined,
        birthDate: Timestamp.fromDate(data.birthDate),
        totalPuppies: data.totalPuppies,
        alivePuppies: data.alivePuppies,
        deliveryType: data.deliveryType,
        notes: data.notes,
        createdAt: now,
      };

      await setDoc(recordRef, birthRecord);

      return birthRecord;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`出産記録の作成に失敗しました: ${error.message}`);
      }
      throw new Error('出産記録の作成に失敗しました');
    }
  }

  /**
   * 出産記録取得（単体）
   */
  async getBirthRecord(recordId: string, userId: string): Promise<BirthRecord | null> {
    try {
      const recordRef = doc(db, this.COLLECTION_NAME, recordId);
      const recordSnap = await getDoc(recordRef);

      if (!recordSnap.exists()) {
        return null;
      }

      const birthRecord = recordSnap.data() as BirthRecord;

      // 自身の記録のみアクセス可能
      if (birthRecord.ownerId !== userId) {
        throw new Error('この出産記録にアクセスする権限がありません');
      }

      return birthRecord;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`出産記録の取得に失敗しました: ${error.message}`);
      }
      throw new Error('出産記録の取得に失敗しました');
    }
  }

  /**
   * 出産記録一覧取得（ソート対応）
   */
  async getBirthRecords(
    userId: string,
    sortBy: 'birthDate' | 'createdAt' = 'birthDate',
    sortOrder: 'asc' | 'desc' = 'desc'
  ): Promise<BirthRecord[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('ownerId', '==', userId),
        orderBy(sortBy, sortOrder),
      ];

      const q = query(collection(db, this.COLLECTION_NAME), ...constraints);
      const querySnapshot = await getDocs(q);

      const birthRecords: BirthRecord[] = [];
      querySnapshot.forEach((doc) => {
        birthRecords.push(doc.data() as BirthRecord);
      });

      return birthRecords;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`出産記録一覧の取得に失敗しました: ${error.message}`);
      }
      throw new Error('出産記録一覧の取得に失敗しました');
    }
  }

  /**
   * メス犬の出産履歴取得
   */
  async getBirthRecordsByMother(motherId: string, userId: string): Promise<BirthRecord[]> {
    try {
      // メス犬の所有確認
      const mother = await dogService.getDog(motherId, userId);
      if (!mother) {
        throw new Error('メス犬が見つかりません');
      }

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('ownerId', '==', userId),
        where('motherId', '==', motherId),
        orderBy('birthDate', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const birthRecords: BirthRecord[] = [];

      querySnapshot.forEach((doc) => {
        birthRecords.push(doc.data() as BirthRecord);
      });

      return birthRecords;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`メス犬の出産履歴の取得に失敗しました: ${error.message}`);
      }
      throw new Error('メス犬の出産履歴の取得に失敗しました');
    }
  }

  /**
   * オス犬の父犬としての記録取得
   */
  async getBirthRecordsByFather(fatherId: string, userId: string): Promise<BirthRecord[]> {
    try {
      // オス犬の所有確認
      const father = await dogService.getDog(fatherId, userId);
      if (!father) {
        throw new Error('オス犬が見つかりません');
      }

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('ownerId', '==', userId),
        where('fatherId', '==', fatherId),
        orderBy('birthDate', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const birthRecords: BirthRecord[] = [];

      querySnapshot.forEach((doc) => {
        birthRecords.push(doc.data() as BirthRecord);
      });

      return birthRecords;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`オス犬の父犬記録の取得に失敗しました: ${error.message}`);
      }
      throw new Error('オス犬の父犬記録の取得に失敗しました');
    }
  }

  /**
   * 出産記録更新
   */
  async updateBirthRecord(
    recordId: string,
    data: Partial<BirthRecordFormData>,
    userId: string
  ): Promise<void> {
    try {
      // 存在確認と権限チェック
      const existingRecord = await this.getBirthRecord(recordId, userId);
      if (!existingRecord) {
        throw new Error('出産記録が見つかりません');
      }

      // 更新データのバリデーション
      const updateData = {
        ...existingRecord,
        ...data,
      };
      this.validateBirthRecordData(updateData as BirthRecordFormData);

      // 親犬が変更された場合は所有確認
      if (data.motherId || data.fatherId) {
        await this.verifyParentOwnership(
          data.motherId || existingRecord.motherId,
          data.fatherId || existingRecord.fatherId,
          userId
        );
      }

      const recordRef = doc(db, this.COLLECTION_NAME, recordId);

      const updatePayload: Record<string, unknown> = {
        ...data,
        matingDate: data.matingDate ? Timestamp.fromDate(data.matingDate) : undefined,
        birthDate: data.birthDate ? Timestamp.fromDate(data.birthDate) : undefined,
      };

      // undefinedの値を削除
      Object.keys(updatePayload).forEach((key) => {
        if (updatePayload[key] === undefined) {
          delete updatePayload[key];
        }
      });

      await updateDoc(recordRef, updatePayload);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`出産記録の更新に失敗しました: ${error.message}`);
      }
      throw new Error('出産記録の更新に失敗しました');
    }
  }

  /**
   * 出産記録削除
   */
  async deleteBirthRecord(recordId: string, userId: string): Promise<void> {
    try {
      // 存在確認と権限チェック
      const existingRecord = await this.getBirthRecord(recordId, userId);
      if (!existingRecord) {
        throw new Error('出産記録が見つかりません');
      }

      const recordRef = doc(db, this.COLLECTION_NAME, recordId);
      await deleteDoc(recordRef);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`出産記録の削除に失敗しました: ${error.message}`);
      }
      throw new Error('出産記録の削除に失敗しました');
    }
  }

  /**
   * 出産記録検索（高度なフィルタリング）
   */
  async searchBirthRecords(
    userId: string,
    filter: BirthRecordSearchFilter
  ): Promise<BirthRecord[]> {
    try {
      // まず全記録を取得
      let birthRecords = await this.getBirthRecords(userId);

      // クライアント側でフィルタリング（Firestoreの複合クエリ制限のため）
      if (filter.motherId) {
        birthRecords = birthRecords.filter((record) => record.motherId === filter.motherId);
      }

      if (filter.fatherId) {
        birthRecords = birthRecords.filter((record) => record.fatherId === filter.fatherId);
      }

      if (filter.deliveryType) {
        birthRecords = birthRecords.filter(
          (record) => record.deliveryType === filter.deliveryType
        );
      }

      if (filter.birthDateFrom) {
        const fromTimestamp = Timestamp.fromDate(filter.birthDateFrom);
        birthRecords = birthRecords.filter(
          (record) => record.birthDate.toMillis() >= fromTimestamp.toMillis()
        );
      }

      if (filter.birthDateTo) {
        const toTimestamp = Timestamp.fromDate(filter.birthDateTo);
        birthRecords = birthRecords.filter(
          (record) => record.birthDate.toMillis() <= toTimestamp.toMillis()
        );
      }

      return birthRecords;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`出産記録検索に失敗しました: ${error.message}`);
      }
      throw new Error('出産記録検索に失敗しました');
    }
  }

  /**
   * 出産予定日計算（交配日から）
   */
  calculateExpectedBirthDate(matingDate: Date): Date {
    const expectedDate = new Date(matingDate);
    expectedDate.setDate(expectedDate.getDate() + this.GESTATION_PERIOD_DAYS);
    return expectedDate;
  }

  /**
   * 出産記録データのバリデーション
   */
  private validateBirthRecordData(data: BirthRecordFormData): void {
    // 総頭数 >= 生存頭数
    if (data.totalPuppies < data.alivePuppies) {
      throw new Error('総頭数は生存頭数以上である必要があります');
    }

    // 出産日 >= 交配日（交配日が設定されている場合）
    if (data.matingDate && data.birthDate < data.matingDate) {
      throw new Error('出産日は交配日以降である必要があります');
    }

    // 頭数は正の整数
    if (data.totalPuppies < 0 || data.alivePuppies < 0) {
      throw new Error('頭数は0以上である必要があります');
    }
  }

  /**
   * 親犬の所有確認
   */
  private async verifyParentOwnership(
    motherId: string,
    fatherId: string,
    userId: string
  ): Promise<void> {
    const mother = await dogService.getDog(motherId, userId);
    const father = await dogService.getDog(fatherId, userId);

    if (!mother) {
      throw new Error('メス犬が見つかりません');
    }

    if (!father) {
      throw new Error('オス犬が見つかりません');
    }

    // 性別確認
    if (mother.gender !== 'female') {
      throw new Error('選択した犬はメスではありません');
    }

    if (father.gender !== 'male') {
      throw new Error('選択した犬はオスではありません');
    }
  }
}

// シングルトンインスタンスをエクスポート
export const birthRecordService = new BirthRecordService();
